import React from 'react';
import Icon from '../../../components/AppIcon';


const VisionSection = () => {
  const visionPoints = [
    {
      icon: "Rocket",
      title: "Democratizing Tech Education",
      description: "Making quality programming education accessible to every aspiring developer in Africa, regardless of their background or location.",
      impact: "10,000+ developers trained by 2030"
    },
    {
      icon: "Globe",
      title: "Global-Local Bridge",
      description: "Creating seamless connections between African talent and global opportunities while preserving cultural identity and local innovation.",
      impact: "500+ international placements annually"
    },
    {
      icon: "Users",
      title: "Sustainable Mentorship Ecosystem",
      description: "Building a self-sustaining network where every mentee becomes a mentor, multiplying impact across generations of developers.",
      impact: "1:10 mentor-to-mentee ratio"
    },
    {
      icon: "Lightbulb",
      title: "Innovation Hubs",
      description: "Establishing technology innovation centers across Cameroon that serve as catalysts for local problem-solving and entrepreneurship.",
      impact: "3+ innovation centers by 2028"
    }
  ];

  const initiatives = [
    {
      title: "CodersHub Academy",
      description: "A comprehensive online platform offering structured learning paths, mentorship matching, and career guidance.",
      status: "In Development",
      timeline: "Launch Q2 2025",
      features: ["Interactive Courses", "1:1 Mentorship", "Career Placement", "Community Support"]
    },
    {
      title: "African Developer Network",
      description: "A continental network connecting developers, sharing opportunities, and fostering collaboration across borders.",
      status: "Planning Phase",
      timeline: "Launch Q4 2025",
      features: ["Cross-border Collaboration", "Opportunity Sharing", "Skill Exchange", "Cultural Bridge"]
    },
    {
      title: "Tech Leadership Program",
      description: "Advanced program for experienced developers to develop leadership skills and community impact strategies.",
      status: "Pilot Phase",
      timeline: "Full Launch Q1 2025",
      features: ["Leadership Training", "Community Building", "Mentorship Skills", "Impact Measurement"]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-secondary via-secondary/95 to-primary/20">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-1 bg-accent rounded-full"></div>
            <span className="text-accent font-medium tracking-wider uppercase text-sm">
              Future Vision
            </span>
            <div className="w-12 h-1 bg-accent rounded-full"></div>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Shaping the
            <span className="text-gradient-gold"> Future</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A vision for the future where technology education is accessible, mentorship is sustainable, and African developers lead global innovation.
          </p>
        </div>

        {/* Vision Points */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {visionPoints?.map((point, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Icon name={point?.icon} size={28} className="text-white" />
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl lg:text-2xl font-bold text-white">
                    {point?.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {point?.description}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Icon name="Target" size={16} className="text-accent" />
                    <span className="text-accent font-medium text-sm">
                      {point?.impact}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Current Initiatives */}
        <div className="space-y-12">
          <div className="text-center">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              Current Initiatives
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Concrete steps being taken today to build the future of tech education and community development.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {initiatives?.map((initiative, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-elevated hover:shadow-2xl transition-all duration-300">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xl font-bold text-secondary">
                        {initiative?.title}
                      </h4>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        initiative?.status === 'In Development' ? 'bg-primary/10 text-primary' :
                        initiative?.status === 'Planning Phase'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                      }`}>
                        {initiative?.status}
                      </div>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {initiative?.description}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-accent" />
                      <span className="text-sm font-medium text-accent">
                        {initiative?.timeline}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h5 className="font-semibold text-secondary">Key Features</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {initiative?.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                          <span className="text-sm text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 lg:p-12">
            <div className="space-y-6">
              <Icon name="Heart" size={48} className="text-accent mx-auto" />
              <h3 className="text-2xl lg:text-3xl font-bold text-white">
                Join the Movement
              </h3>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                The future of tech education and community development is being built today. Be part of this transformative journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => window.location.href = '/contactpage'}
                  className="px-8 py-3 bg-accent text-secondary font-semibold rounded-lg hover:bg-accent/90 transition-colors duration-300 flex items-center space-x-2"
                >
                  <span>Get Involved</span>
                  <Icon name="ArrowRight" size={16} />
                </button>
                <button
                  onClick={() => window.location.href = '/blogpage'}
                  className="px-8 py-3 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors duration-300 flex items-center space-x-2"
                >
                  <span>Learn More</span>
                  <Icon name="BookOpen" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Vision Quote */}
        <div className="mt-16 text-center">
          <div className="max-w-4xl mx-auto">
            <Icon name="Quote" size={48} className="text-accent/30 mx-auto mb-6" />
            <blockquote className="text-2xl lg:text-3xl font-medium text-white leading-relaxed mb-6">
              "The future belongs to those who believe in the beauty of their dreams and have the courage to turn them into reality."
            </blockquote>
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-1 bg-accent rounded-full"></div>
              <span className="text-gray-300 font-medium">Adrian's Vision for 2030</span>
              <div className="w-12 h-1 bg-accent rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;